import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import payrollService from '../../../services/payrollService';
import { AlertCircle, Download, Printer, Calendar, Building2, User, FileText, Link as LinkIcon, CheckCircle, Info } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import { formatEasternDateOnly } from '../../../utils/timezone';

export default function HRReportsSalarySlips() {
  const navigate = useNavigate();
  const slipRef = useRef(null);

  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [salarySlip, setSalarySlip] = useState(null);
  const [payPeriodStart, setPayPeriodStart] = useState('');
  const [payPeriodEnd, setPayPeriodEnd] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generating, setGenerating] = useState(false);
  const [showEmailInfo, setShowEmailInfo] = useState(false);

  useEffect(() => {
    loadEmployees();
    
    // Set default pay period (current month)
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setPayPeriodStart(firstDay?.toISOString()?.split('T')?.[0]);
    setPayPeriodEnd(lastDay?.toISOString()?.split('T')?.[0]);
  }, []);

  useEffect(() => {
    if (selectedEmployee && payPeriodStart && payPeriodEnd) {
      loadSalarySlip();
    }
  }, [selectedEmployee, payPeriodStart, payPeriodEnd]);

  const loadEmployees = async () => {
    try {
      setError('');
      const data = await payrollService?.getAllEmployeesWithSalary();
      setEmployees(data);
    } catch (err) {
      setError(err?.message || 'Failed to load employees');
      console.error('Load employees error:', err);
    }
  };

  const loadSalarySlip = async () => {
    try {
      setLoading(true);
      setError('');
      
      const employee = employees?.find(e => e?.employeeId === selectedEmployee);
      if (!employee) {
        setError('Employee not found');
        return;
      }

      const slip = await payrollService?.getEmployeeSalarySlip(
        employee?.id,
        payPeriodStart,
        payPeriodEnd
      );

      if (!slip) {
        setError('No salary record found for this period. Please process payroll first.');
        setSalarySlip(null);
      } else {
        setSalarySlip(slip);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load salary slip');
      console.error('Load salary slip error:', err);
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async () => {
    if (!salarySlip || !slipRef?.current) return;

    try {
      setGenerating(true);
      setSuccess('');
      
      const canvas = await html2canvas(slipRef?.current, {
        scale: 2,
        logging: false,
        useCORS: true
      });

      const imgData = canvas?.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf?.internal?.pageSize?.getWidth();
      const pdfHeight = pdf?.internal?.pageSize?.getHeight();
      const imgWidth = canvas?.width;
      const imgHeight = canvas?.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;

      pdf?.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf?.save(`salary-slip-${salarySlip?.employeeId}-${payPeriodStart}.pdf`);
      
      setSuccess('Salary slip PDF downloaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to generate PDF: ' + err?.message);
      console.error('PDF generation error:', err);
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCopyLink = () => {
    if (!salarySlip) return;
    
    const link = `${window?.location?.origin}/salary-slip-generator?employeeId=${selectedEmployee}&start=${payPeriodStart}&end=${payPeriodEnd}`;
    
    navigator?.clipboard?.writeText(link)?.then(() => {
      setSuccess('Salary slip link copied to clipboard! You can share this link.');
      setTimeout(() => setSuccess(''), 3000);
    })?.catch(err => {
      setError('Failed to copy link: ' + err?.message);
    });
  };

  const handleViewFullPage = () => {
    navigate(`/salary-slip-generator?employeeId=${selectedEmployee}&start=${payPeriodStart}&end=${payPeriodEnd}`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return formatEasternDateOnly(dateString, {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getMonthYear = (dateString) => {
    if (!dateString) return '';
    return formatEasternDateOnly(dateString, {
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-600" />
              Salary Slip Generator
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Generate and download professional salary slips for employees
            </p>
          </div>
          <Button
            onClick={handleViewFullPage}
            variant="outline"
            size="sm"
            disabled={!salarySlip}
          >
            <FileText className="w-4 h-4 mr-2" />
            Open Full Page
          </Button>
        </div>

        {/* Email Feature Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-blue-900 mb-1">Email Feature Available</h4>
              <p className="text-sm text-blue-800 mb-2">
                To email salary slips directly to employees, you'll need to set up Resend API integration.
              </p>
              <button
                onClick={() => setShowEmailInfo(!showEmailInfo)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium underline"
              >
                {showEmailInfo ? 'Hide' : 'Show'} setup instructions
              </button>
              
              {showEmailInfo && (
                <div className="mt-3 p-3 bg-white rounded border border-blue-200">
                  <h5 className="font-semibold text-gray-900 mb-2">Setup Instructions:</h5>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                    <li>Sign up for a Resend account at <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">resend.com</a></li>
                    <li>Get your API key from the Resend dashboard</li>
                    <li>Add <code className="bg-gray-100 px-1 rounded">VITE_RESEND_API_KEY</code> to your .env file</li>
                    <li>Restart your development server</li>
                    <li>The email button will automatically become available</li>
                  </ol>
                  <p className="text-xs text-gray-600 mt-2">
                    For now, you can download PDFs and share links as alternatives.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-green-800">{success}</p>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-800">
              ×
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
              ×
            </button>
          </div>
        )}

        {/* Selection Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <User className="w-4 h-4 inline mr-1" />
              Select Employee
            </label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">Choose Employee</option>
              {employees?.map(emp => (
                <option key={emp?.employeeId} value={emp?.employeeId}>
                  {emp?.employeeId} - {emp?.fullName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="w-4 h-4 inline mr-1" />
              Period Start
            </label>
            <input
              type="date"
              value={payPeriodStart}
              onChange={(e) => setPayPeriodStart(e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="w-4 h-4 inline mr-1" />
              Period End
            </label>
            <input
              type="date"
              value={payPeriodEnd}
              onChange={(e) => setPayPeriodEnd(e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          <div className="flex items-end">
            <Button
              onClick={generatePDF}
              disabled={!salarySlip || generating}
              className="w-full"
              size="sm"
            >
              <Download className="w-4 h-4 mr-1" />
              {generating ? 'Generating...' : 'Download PDF'}
            </Button>
          </div>

          <div className="flex items-end gap-2">
            <Button
              onClick={handleCopyLink}
              disabled={!salarySlip}
              variant="outline"
              className="flex-1"
              size="sm"
            >
              <LinkIcon className="w-4 h-4 mr-1" />
              Share Link
            </Button>
            <Button
              onClick={handlePrint}
              disabled={!salarySlip}
              variant="outline"
              size="sm"
            >
              <Printer className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Salary Slip Preview */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-500">Loading salary slip...</p>
        </div>
      ) : !salarySlip ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">
            {selectedEmployee && payPeriodStart && payPeriodEnd
              ? 'No salary record found for this period'
              : 'Select employee and pay period to view salary slip'}
          </p>
        </div>
      ) : (
        <div ref={slipRef} className="bg-white rounded-lg shadow-lg border-2 border-gray-300 p-8">
          {/* Company Header */}
          <div className="text-center border-b-2 border-gray-800 pb-6 mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">HR Portal Pro</h2>
            <p className="text-gray-600">Human Resource Management System</p>
            <p className="text-sm text-gray-500 mt-2">
              <Building2 className="w-4 h-4 inline mr-1" />
              Corporate Office, Business District, City
            </p>
          </div>

          {/* Slip Title */}
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-1">SALARY SLIP</h3>
            <p className="text-lg text-gray-700">
              For the month of {getMonthYear(payPeriodStart)}
            </p>
          </div>

          {/* Employee Details */}
          <div className="grid grid-cols-2 gap-6 mb-6 p-4 bg-gray-50 rounded-lg">
            <div>
              <div className="mb-3">
                <p className="text-xs text-gray-500 uppercase">Employee ID</p>
                <p className="text-sm font-semibold text-gray-900">{salarySlip?.employeeId}</p>
              </div>
              <div className="mb-3">
                <p className="text-xs text-gray-500 uppercase">Employee Name</p>
                <p className="text-sm font-semibold text-gray-900">{salarySlip?.fullName}</p>
              </div>
              <div className="mb-3">
                <p className="text-xs text-gray-500 uppercase">Department</p>
                <p className="text-sm font-semibold text-gray-900">{salarySlip?.department}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Designation</p>
                <p className="text-sm font-semibold text-gray-900">{salarySlip?.position}</p>
              </div>
            </div>

            <div>
              <div className="mb-3">
                <p className="text-xs text-gray-500 uppercase">Date of Joining</p>
                <p className="text-sm font-semibold text-gray-900">{formatDate(salarySlip?.dateOfJoining)}</p>
              </div>
              <div className="mb-3">
                <p className="text-xs text-gray-500 uppercase">PF Number</p>
                <p className="text-sm font-semibold text-gray-900">{salarySlip?.pfNumber || 'N/A'}</p>
              </div>
              <div className="mb-3">
                <p className="text-xs text-gray-500 uppercase">ESI Number</p>
                <p className="text-sm font-semibold text-gray-900">{salarySlip?.esiNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Pay Period</p>
                <p className="text-sm font-semibold text-gray-900">
                  {formatDate(payPeriodStart)} to {formatDate(payPeriodEnd)}
                </p>
              </div>
            </div>
          </div>

          {/* Salary Breakdown */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Earnings */}
            <div>
              <div className="bg-green-50 p-3 rounded-t-lg border-b-2 border-green-600">
                <h4 className="font-bold text-green-900 uppercase">Earnings</h4>
              </div>
              <div className="border border-gray-200 rounded-b-lg">
                <div className="p-3 border-b border-gray-200 flex justify-between">
                  <span className="text-sm text-gray-700">Basic Salary</span>
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.baseSalary)}</span>
                </div>
                <div className="p-3 border-b border-gray-200 flex justify-between">
                  <span className="text-sm text-gray-700">Allowances</span>
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.allowances)}</span>
                </div>
                <div className="p-3 border-b border-gray-200 flex justify-between">
                  <span className="text-sm text-gray-700">Bonuses</span>
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.bonuses)}</span>
                </div>
                <div className="p-3 border-b border-gray-200 flex justify-between">
                  <span className="text-sm text-gray-700">Incentives</span>
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.incentives)}</span>
                </div>
                <div className="p-3 border-b border-gray-200 flex justify-between">
                  <span className="text-sm text-gray-700">Overtime Pay ({salarySlip?.overtimeHours}h)</span>
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.overtimePay)}</span>
                </div>
                <div className="p-3 bg-green-100 rounded-b-lg flex justify-between">
                  <span className="text-sm font-bold text-green-900">GROSS PAY</span>
                  <span className="text-sm font-bold text-green-900">{formatCurrency(salarySlip?.grossPay)}</span>
                </div>
              </div>
            </div>

            {/* Deductions */}
            <div>
              <div className="bg-red-50 p-3 rounded-t-lg border-b-2 border-red-600">
                <h4 className="font-bold text-red-900 uppercase">Deductions</h4>
              </div>
              <div className="border border-gray-200 rounded-b-lg">
                <div className="p-3 border-b border-gray-200 flex justify-between">
                  <span className="text-sm text-gray-700">PF Contribution</span>
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.deductions * 0.4)}</span>
                </div>
                <div className="p-3 border-b border-gray-200 flex justify-between">
                  <span className="text-sm text-gray-700">ESI Contribution</span>
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.deductions * 0.2)}</span>
                </div>
                <div className="p-3 border-b border-gray-200 flex justify-between">
                  <span className="text-sm text-gray-700">Professional Tax</span>
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.deductions * 0.1)}</span>
                </div>
                <div className="p-3 border-b border-gray-200 flex justify-between">
                  <span className="text-sm text-gray-700">Income Tax (TDS)</span>
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.taxDeductions)}</span>
                </div>
                <div className="p-3 border-b border-gray-200 flex justify-between">
                  <span className="text-sm text-gray-700">Other Deductions</span>
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.deductions * 0.3)}</span>
                </div>
                <div className="p-3 bg-red-100 rounded-b-lg flex justify-between">
                  <span className="text-sm font-bold text-red-900">TOTAL DEDUCTIONS</span>
                  <span className="text-sm font-bold text-red-900">
                    {formatCurrency(salarySlip?.deductions + salarySlip?.taxDeductions)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Net Pay */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 rounded-lg mb-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-lg font-medium opacity-90">NET SALARY</p>
                <p className="text-sm opacity-75">(Amount payable)</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">{formatCurrency(salarySlip?.netPay)}</p>
              </div>
            </div>
          </div>

          {/* Bank Details */}
          {(salarySlip?.bankName || salarySlip?.accountNumber) && (
            <div className="p-4 bg-blue-50 rounded-lg mb-6">
              <h4 className="font-bold text-blue-900 mb-3">BANK DETAILS</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-600 uppercase">Bank Name</p>
                  <p className="text-sm font-semibold text-gray-900">{salarySlip?.bankName || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 uppercase">Account Number</p>
                  <p className="text-sm font-semibold text-gray-900">{salarySlip?.accountNumber || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 uppercase">IFSC Code</p>
                  <p className="text-sm font-semibold text-gray-900">{salarySlip?.ifscCode || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="border-t-2 border-gray-300 pt-6 mt-6">
            <div className="flex justify-between items-end">
              <div>
                <p className="text-xs text-gray-500">Generated on: {formatDate(new Date()?.toISOString())}</p>
                {salarySlip?.payrollNotes && (
                  <p className="text-xs text-gray-600 mt-2 italic">Note: {salarySlip?.payrollNotes}</p>
                )}
              </div>
              <div className="text-right">
                <div className="border-t-2 border-gray-800 w-48 mb-2"></div>
                <p className="text-sm font-semibold text-gray-900">Authorized Signature</p>
                <p className="text-xs text-gray-500">HR Department</p>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-6 p-3 bg-gray-100 rounded text-center">
            <p className="text-xs text-gray-600">
              This is a computer-generated salary slip and does not require a physical signature.
              For any queries, please contact the HR department.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}